<script setup lang="ts">
import Live from "@/ui/components/buttons/Live.vue";
import useDialog from "@/ui/composables/useDialog";
import { computed, ref, Ref, watch } from "vue";
import { toastApiResult } from "@/toaster";
import { useApiClient } from "@/api/client";
import FileDTO = App.Data.FileDTO;
import { useDashboardStore } from "@/modules/dashboards/store";
import DateChip from "@/ui/components/date/DateChip.vue";
import DateSelectionWrapper from "@/modules/explorer/components/DateSelectionWrapper.vue";
import SnapshotSelectionWrapper from "@/modules/explorer/components/SnapshotSelectionWrapper.vue";
import { downloadDashboardAsPdf } from "@/modules/dashboards/components/DownloadDashboard";
import { useDashboardState } from "@/modules/dashboards/components/DashboardState";
import NewDashboardScheduleDialog from "@/modules/dashboards/components/NewDashboardScheduleDialog.vue";
import DashboardSettingsDialog from "@/modules/dashboards/components/DashboardSettingsDialog.vue";
import { useBaseStore } from "@/stores/base";
import FilterBar from "@/modules/explorer/components/FilterBar.vue";
import { ActivePin } from "@/modules/explorer/explorerTypes";
import { canPrintDashboard } from "@/modules/dashboards/dashboard-utils";
import { promptInvalidPermissions } from "@/api/helpers/loadingWrapper";
import ResourceShareDialog from "@/ui/components/FileBrowser/ResourceShareDialog.vue";
import { useResourceShare } from "@/ui/components/FileBrowser/useResourceShare";
import PublicUserDO = App.Data.PublicUserDO;
import RoleDO = App.Data.RoleDO;
import AbilityName = App.Enums.AbilityName;
import DashboardAiSettingsDO = App.Data.DashboardAiSettingsDO;
import QuickFilterCard from "@/modules/explorer/components/QuickFilter/QuickFilterCard.vue";
import { onBeforeRouteLeave } from "vue-router";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import dayjs from "dayjs";

const {
	isEqual,
	cardsForWatcher,
	dashboardForWatcher,
	computedPeriodSelection,
} = useDashboardState();

const dialog = useDialog();
const isNewDashboardScheduleDialogVisible: Ref<boolean> = ref(false);
const isDashboardSettingsDialogVisible: Ref<boolean> = ref(false);

const dashboardState = useDashboardStore().dashboardState;

const isDateSelectionVisible = ref(false);
const isSnapShotSelectionVisible = ref(false);
const isDashboardChanged = ref(false);

const formattedTimeContext = computed(() => {
	const parsed = dayjs(dashboardState.dashboard.time_context_parsed);
	return parsed.isSame(parsed.startOf("day"))
		? parsed.format("D MMM YYYY")
		: parsed.format("D MMM YYYY HH:mm:ss");
});

const isAiAssistEnabled = computed(() => {
	return (
		useBaseStore().HAS_ENVIRONMENT_FEATURE("environment_supports_ai_assist") &&
		useBaseStore().currentUser?.derivedPermissions.includes("can_ask_ai")
	);
});

async function saveDashboard() {
	const choice = await dialog.confirm({
		title: "Save Dashboard",
		message: "Are you sure you want to save the dashboard?",
	});

	if (choice) {
		toastApiResult(
			promptInvalidPermissions(
				useApiClient("dashboard").set(
					dashboardState.dashboard.uuid,
					dashboardState.dashboard
				)
			)
		);
		isDashboardChanged.value = false;
	}
	dashboardState.editorMode = "view";
}

const printMode = computed(() => dashboardState.dashboard.mode === "print");

const isPrintable = computed(() => {
	return canPrintDashboard(dashboardState.dashboard);
});

const toggleEditMode = async () => {
	if (dashboardState.editorMode === "view") {
		if (dashboardState.previewZoomFactor > 1 && printMode.value) {
			const choice = await dialog.confirm({
				title: "Reset Zoom",
				message: "Editing can only be done in 1X zoom, reset zoom to 1X?",
			});
			if (!choice) return;
		}

		dashboardState.previewZoomFactor = 1.0;
		dashboardState.isLive = false;
		dashboardState.editorMode = "edit";
	} else {
		dashboardState.editorMode = "view";
	}
};

watch(dashboardForWatcher, () => {
	isDashboardChanged.value = true;
});

watch(cardsForWatcher, (v, ov) => {
	if (!isEqual(v, ov)) {
		isDashboardChanged.value = true;
	}
});

watch(
	() => dashboardState.dashboard.active_filters,
	() => {
		isDashboardChanged.value = true;
	},
	{ deep: true }
);

watch(isDashboardChanged, (v) => {
	dashboardState.dashboardHasChanges = v;
});

watch(
	() => dashboardState.dashboardHasChanges,
	(v) => {
		if (isDashboardChanged.value || v) {
			isDashboardChanged.value = true;
		}
	}
);

function updateAiSettings(value: DashboardAiSettingsDO) {
	dashboardState.dashboard.ai_settings = {
		...dashboardState.dashboard.ai_settings,
		...value,
	};
}
const downloadPdf = async () => {
	downloadDashboardAsPdf(dashboardState.dashboard);
};

const userCanScheduleExport = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_scheduler"
	);
});

const visiblePins = computed(() => {
	return useDashboardStore().dashboardState.activePins.filter(
		(activePin: ActivePin) =>
			!useDashboardStore().activeDashboardFilters.includes(
				activePin.dimension.name_clean
			)
	);
});

const activeFilters = computed(() => {
	return useDashboardStore().dashboardState.dashboard.active_filters;
});

const openQuickFilters = computed(() =>
	dashboardState.dashboard.active_filters.filter(
		(active_filter) => active_filter.isQuickFilter && active_filter.isApplied
	)
);

const isShareResourceDialogVisible = ref<boolean>(false);

const listUsersWithSharePermissionsOnDashboard = async (resource: FileDTO) => {
	return await useApiClient("dashboard").getUsersWithPermissionsToDashboard(
		resource
	);
};

const grantPermissionsToUserOnDashboard = async (
	resource: FileDTO,
	user: PublicUserDO,
	ability: AbilityName
) => {
	return await useApiClient("dashboard").grantPermissionsToDashboardForUser(
		resource,
		user,
		ability
	);
};

const revokePermissionsForUserOnDashboard = async (
	resource: FileDTO,
	user: PublicUserDO
) => {
	return await useApiClient("dashboard").revokePermissionsToDashboardForUser(
		resource,
		user
	);
};

const listRolesWithSharePermissionsOnDashboard = async (resource: FileDTO) => {
	return await useApiClient("dashboard").getRolesWithPermissionsToDashboard(
		resource
	);
};

const grantPermissionsToRoleOnDashboard = async (
	resource: FileDTO,
	role: RoleDO,
	ability: AbilityName
) => {
	return await useApiClient("dashboard").grantPermissionsToDashboardForRole(
		resource,
		role,
		ability
	);
};

const revokePermissionsForRoleOnDashboard = async (
	resource: FileDTO,
	role: RoleDO
) => {
	return await useApiClient("dashboard").revokePermissionsToDashboardForRole(
		resource,
		role
	);
};

useResourceShare(
	listUsersWithSharePermissionsOnDashboard,
	grantPermissionsToUserOnDashboard,
	revokePermissionsForUserOnDashboard,
	listRolesWithSharePermissionsOnDashboard,
	grantPermissionsToRoleOnDashboard,
	revokePermissionsForRoleOnDashboard
);

watch(
	() => dashboardState.dashboard.legend_width,
	(fixedLegendWidth) => {
		dashboardState.dashboard.cards.forEach((card) => {
			if (card.stored_document?.activeDocument) {
				card.stored_document.activeDocument.legend_width = fixedLegendWidth;
			}
		});
	},
	{ immediate: true }
);

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog(
		"dashboard",
		dashboardState.dashboardHasChanges,
		next,
		() => (dashboardState.dashboardHasChanges = false)
	);
});
</script>

<template>
	<div class="sticky-header bg-background top-px-mask mb-3">
		<new-dashboard-schedule-dialog
			v-if="isNewDashboardScheduleDialogVisible"
			v-model="isNewDashboardScheduleDialogVisible"
			:dashboard_uuid="dashboardState.dashboard.uuid"
		/>

		<resource-share-dialog
			v-if="isShareResourceDialogVisible"
			v-model="isShareResourceDialogVisible"
			:resource-to-manage="dashboardState.dashboard"
		/>

		<dashboard-settings-dialog
			v-if="isDashboardSettingsDialogVisible"
			v-model="isDashboardSettingsDialogVisible"
		/>

		<!-- Toolbar -->
		<v-card class="px-2">
			<div class="d-flex align-center ga-2" style="height: 40px">
				<!-- Date Selection Menu - Now outside of mode-specific templates -->
				<Live v-model="dashboardState.isLive" />
				<v-divider vertical class="mt-2" />
				<h3>
					{{ dashboardState.dashboard.title }}
				</h3>
				<v-spacer />

				<v-menu
					v-model="isDateSelectionVisible"
					:close-on-content-click="false"
					location="bottom right"
					:disabled="
						dashboardState.editorMode === 'edit' ||
						(!dashboardState.dashboard.use_single_subject_date &&
							!dashboardState.dashboard.use_time_context)
					"
				>
					<template v-slot:activator="{ props }">
						<date-chip
							v-if="dashboardState.dashboard.use_single_subject_date"
							v-bind="props"
							:input-value="computedPeriodSelection"
							:disabled="dashboardState.editorMode === 'edit'"
						/>
						<v-chip
							v-else
							label
							size="small"
							v-bind="props"
							:disabled="
								!dashboardState.dashboard.use_single_subject_date &&
								!dashboardState.dashboard.use_time_context
							"
						>
							<span
								v-if="
									dashboardState.dashboard.use_time_context &&
									dashboardState.dashboard.time_context !== 'now'
								"
								>{{ formattedTimeContext }}</span
							>
							<span v-else>Cards use their individual date-configuration.</span>
						</v-chip>
					</template>
					<v-card width="600">
						<v-toolbar style="border-radius: 0 !important" color="white" flat>
							<template v-slot:prepend>
								<v-toolbar-title class="pt-1 ml-2" style="font-size: 16px"
									>Subject Date Configuration</v-toolbar-title
								>
							</template>
						</v-toolbar>
						<v-divider />
						<date-selection-wrapper
							v-if="dashboardState.dashboard.use_single_subject_date"
							v-model="computedPeriodSelection"
							@close="isDateSelectionVisible = false"
							:show-presets="true"
							:nullable="false"
						/>
						<div v-else>
							<div
								v-if="dashboardState.dashboard.use_time_context"
								class="px-5 mt-5"
							>
								<snapshot-selection-wrapper
									v-if="dashboardState.dashboard.use_time_context"
									:is-time-context="true"
									v-model="isSnapShotSelectionVisible"
									v-model:period_measurement="
										dashboardState.dashboard.time_context
									"
									v-model:period_measurement_parsed="
										dashboardState.dashboard.time_context_parsed
									"
								/>
							</div>
							<v-alert
								color="primary"
								variant="tonal"
								border
								class="mx-2 my-5 text-caption"
							>
								All cards will use their individually saved subject-date
								configuration.
								<span v-if="dashboardState.dashboard.use_time_context"
									>However, ‘<i><b>now</b></i
									>’ will be interpreted as ‘<i
										><b> {{ dashboardState.dashboard.time_context }}</b></i
									>’</span
								>. This will
								<b
									><u>{{
										dashboardState.dashboard.use_time_context_for_snapshot_date
											? "also"
											: "not"
									}}</u></b
								>
								be applied to the card's snapshot dates.
							</v-alert>
							<v-divider />
							<v-card-actions>
								<v-spacer />
								<v-btn
									variant="text"
									color="error"
									@click="isDateSelectionVisible = false"
									>Close
								</v-btn>
							</v-card-actions>
						</div>
					</v-card>
				</v-menu>

				<!--		Snapshot selection -->
				<v-menu
					v-if="dashboardState.dashboard.use_single_measure_date"
					v-model="isSnapShotSelectionVisible"
					:close-on-content-click="false"
					location="bottom right"
					:disabled="dashboardState.editorMode === 'edit'"
				>
					<template v-slot:activator="{ props }">
						<v-btn
							v-bind="props"
							icon
							variant="text"
							class="ml-3"
							size="32"
							:disabled="dashboardState.editorMode === 'edit'"
							color="on-surface"
						>
							<i class="fa-light fa-timeline-arrow fa-lg"></i>
						</v-btn>
					</template>
					<v-card width="400">
						<v-toolbar style="border-radius: 0 !important" color="white" flat>
							<template v-slot:prepend>
								<v-toolbar-title class="pt-1 ml-2" style="font-size: 16px"
									>Snapshot Date Configuration</v-toolbar-title
								>
							</template>
						</v-toolbar>
						<v-divider />
						<snapshot-selection-wrapper
							v-if="dashboardState.dashboard.use_single_measure_date"
							v-model="isSnapShotSelectionVisible"
							v-model:period_measurement="
								dashboardState.dashboard.period_measurement
							"
							v-model:period_measurement_parsed="
								dashboardState.dashboard.period_measurement_parsed
							"
						/>
						<div v-else>
							<v-alert
								color="primary"
								variant="tonal"
								border
								class="mx-2 my-5 text-caption"
							>
								All cards will use their individually saved snapshot-date
								configuration
							</v-alert>
							<v-divider />
							<v-card-actions>
								<v-spacer />
								<v-btn
									variant="text"
									color="error"
									@click="isSnapShotSelectionVisible = false"
									>Close
								</v-btn>
							</v-card-actions>
						</div>
					</v-card>
				</v-menu>

				<v-divider vertical class="mt-2" />
				<v-btn
					v-if="useDashboardStore().userCanEdit"
					v-tooltip="`Edit Layout`"
					size="32"
					@click="toggleEditMode"
					variant="text"
					:active="dashboardState.editorMode === 'edit'"
					color="on-surface"
				>
					<v-icon>mdi-resize</v-icon>
				</v-btn>

				<!--			<ai-settings-flow-->
				<!--				v-if="printMode && isAiAssistEnabled"-->
				<!--				:model-value="dashboardState.dashboard.ai_settings"-->
				<!--				@update:model-value="updateAiSettings"-->
				<!--			/>-->
				<v-menu
					location="bottom right"
					v-if="printMode"
					:close-on-content-click="false"
				>
					<template v-slot:activator="{ props }">
						<v-btn
							style="height: 32px"
							class="pl-3 pr-2"
							color="on-surface"
							v-bind="props"
							variant="text"
							prepend-icon="fa: fa-regular fa-magnifying-glass"
							:disabled="dashboardState.editorMode === 'edit'"
						>
							{{ dashboardState.previewZoomFactor }} X
						</v-btn>
					</template>
					<v-card width="300">
						<v-card-title>Adjust Zoom</v-card-title>
						<v-card-text>
							Use the zoom to make the preview larger or smaller. This only
							affects the web preview and has no impact on the exported PDF.
							<v-number-input
								v-model="dashboardState.previewZoomFactor"
								prepend-inner-icon="mdi-magnify"
								:max="5.0"
								:min="1.0"
								:precision="1"
								:step="0.5"
								class="mt-3"
								density="compact"
								control-variant="stacked"
								inset
								hide-details
								:clearable="dashboardState.previewZoomFactor !== 1.0"
								@click:clear="dashboardState.previewZoomFactor = 1.0"
							/>
						</v-card-text>
					</v-card>
				</v-menu>

				<v-divider vertical class="mt-2" />

				<v-btn
					v-if="useDashboardStore().userCanEdit"
					size="32"
					color="on-surface"
					variant="text"
					:disabled="dashboardState.editorMode === 'edit'"
					:active="isDashboardSettingsDialogVisible"
					@click="isDashboardSettingsDialogVisible = true"
				>
					<v-icon icon="fa: fa-light fa-cog" />
				</v-btn>

				<div v-if="useDashboardStore().userCanEdit">
					<v-badge
						:model-value="isDashboardChanged"
						color="error"
						offset-x="4"
						offset-y="4"
						dot
					>
						<v-btn
							size="32"
							@click="saveDashboard"
							variant="text"
							v-tooltip="`Save Dashboard`"
							color="on-surface"
							data-testid="save-dashboard"
						>
							<v-icon size="20" icon="fa: fa-solid fa-floppy-disk" />
						</v-btn>
					</v-badge>
				</div>

				<v-menu location="bottom right">
					<template v-slot:activator="{ props }">
						<v-btn color="on-surface" size="32" v-bind="props" variant="text">
							<i class="fa-regular fa-ellipsis-vertical"></i>
						</v-btn>
					</template>
					<v-list class="pa-1 mt-2">
						<v-list-item
							class="font-weight-regular text-caption"
							@click="isDashboardSettingsDialogVisible = true"
							:disabled="!useDashboardStore().userCanEdit"
						>
							<template v-slot:prepend>
								<i v-if="printMode" class="fa fa-regular fa-desktop mr-3"></i>
								<i v-else class="fa fa-regular fa-print mr-3"></i>
							</template>
							Change dashboard-mode
						</v-list-item>

						<v-list-item
							class="font-weight-regular text-caption"
							:disabled="isDashboardChanged"
							@click="isShareResourceDialogVisible = true"
						>
							<template v-slot:prepend>
								<i class="fa fa-light fa-share mr-3"></i>
							</template>
							Share this dashboard with a colleague
						</v-list-item>

						<v-list-item
							v-if="printMode"
							class="font-weight-regular text-caption"
							@click="downloadPdf"
							:disabled="
								!dashboardState.dashboard.id ||
								!isPrintable ||
								isDashboardChanged
							"
						>
							<template v-slot:prepend>
								<i class="fa fa-download mr-3"></i>
							</template>
							Export dashboard as PDF
						</v-list-item>

						<v-list-item
							v-if="userCanScheduleExport && printMode"
							class="font-weight-regular text-caption"
							:disabled="
								!dashboardState.dashboard.id ||
								!isPrintable ||
								isDashboardChanged
							"
							@click="isNewDashboardScheduleDialogVisible = true"
						>
							<template v-slot:prepend>
								<i class="fa fa-calendar-clock mr-3"></i>
							</template>
							Schedule automated export
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
			<v-divider />
			<filter-bar
				style="height: 40px"
				surface="dashboard"
				:active-filters="activeFilters"
				:visible-pins="visiblePins"
			/>
		</v-card>

		<teleport to="#secondary-drawer">
			<component
				:is="useBaseStore().secondaryDrawerComponent"
				v-if="useBaseStore().showSecondaryDrawer"
			/>
		</teleport>

		<QuickFilterCard :quick-filters="openQuickFilters" />
	</div>
</template>

<style scoped lang="scss">
// 61px is the height of the v-app-bar in Header.vue, if it changes, this needs to be adjusted.
// (45px + 1rem)
.sticky-header {
	position: sticky;
	top: 61px; // when it's 60 we loose the boarder when scrolling
	z-index: 100;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}
// now that its 61 top we see the dashboard in a 1px row above the header, fix
.top-px-mask {
	box-shadow: 0 -1px 0 0 rgb(var(--color-background));
}
</style>
